import { useMemo } from 'react'

import { RefetchDevicesFunction, useFloorPlan } from '../../hooks/useFloorPlan'

import {
  TailgateGridModal,
  TailgateGridProvider,
} from '@/features/tailgate-grid'
import { UpdateTailgateEnabledInput } from '@/graphql/generated/schemas'
import { BUTTON_PRESS } from '@/utils/analytics'

import { ConnectionI, DeviceI } from '../../types/types'
import { getById } from '../utils/utils'
import { AssociateDeviceModal } from './AssociateDeviceModal'

interface TailgateConnectionModalsIProps {
  connections: ConnectionI[]
  isConnectionsLoading: boolean
  connectDevices: (
    camera: DeviceI,
    door: DeviceI,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  disconnectDevices: (
    cameraId: string,
    doorId: string,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  getConnection: (cameraId: string, doorId: string) => ConnectionI
}

export const TailgateConnectionModals = ({
  connections,
  isConnectionsLoading,
  connectDevices,
  disconnectDevices,
  getConnection,
}: TailgateConnectionModalsIProps) => {
  const {
    associateDeviceValues,
    devices,
    isAssociateDeviceOpen,
    isTailgateGridOpen,
    isEnableTailgateLoading,
    enableTailgate,
    onAssociateDeviceClose,
    onTailgateGridClose,
    onTailgateGridOpen,
    refetchDevices,
    trackTailgateEvent,
  } = useFloorPlan()

  const handleOnTailgateGridOpen = () => {
    onTailgateGridOpen()
    trackTailgateEvent(`${BUTTON_PRESS} Configure Tailgate grid`)
  }

  const disassociateSelectedDevices = async () => {
    const { doorId, cameraId } = associateDeviceValues
    await disconnectDevices(cameraId, doorId, refetchDevices)
    trackTailgateEvent(`${BUTTON_PRESS} Disassociate devices`)
  }

  const associateSelectedDevices = async () => {
    const { cameraId, doorId } = associateDeviceValues
    const alreadyConnected = getConnection(cameraId, doorId)
    if (alreadyConnected) return

    const camera = getById(devices, cameraId)
    const door = getById(devices, doorId)
    await connectDevices(camera, door, refetchDevices)
    trackTailgateEvent(`${BUTTON_PRESS} Associate devices`)
  }

  const handleEnableTailgate = async (isTailgateEnabled: boolean) => {
    const { doorId, cameraId } = associateDeviceValues
    const input: UpdateTailgateEnabledInput = {
      cameraId,
      doorId,
      isTailgateEnabled,
    }
    await enableTailgate(input, refetchDevices)
  }

  const connection = useMemo(
    () =>
      getConnection(
        associateDeviceValues?.cameraId,
        associateDeviceValues?.doorId
      ),
    [connections, associateDeviceValues]
  )

  const shouldRenderDummyPlayer = isTailgateGridOpen

  return (
    <>
      {isAssociateDeviceOpen && (
        <AssociateDeviceModal
          associateDevice={associateDeviceValues}
          connection={connection}
          hideAssociateButton={false}
          isEnableTailgateLoading={isEnableTailgateLoading}
          isOpen={isAssociateDeviceOpen}
          loading={isConnectionsLoading}
          onAssociate={associateSelectedDevices}
          onClose={onAssociateDeviceClose}
          onDisassociate={disassociateSelectedDevices}
          onEnableTailgate={handleEnableTailgate}
          onOpenGrid={handleOnTailgateGridOpen}
        />
      )}
      <TailgateGridProvider
        associateDeviceValues={associateDeviceValues}
        isGridConfigured={connection?.isTailgateGridConfigured}
        isTailgateGridOpen={isTailgateGridOpen}
        onTailgateGridClose={onTailgateGridClose}
        shouldRenderDummyPlayer={shouldRenderDummyPlayer}
        trackTailgateEvent={trackTailgateEvent}
      >
        <TailgateGridModal />
      </TailgateGridProvider>
    </>
  )
}
